import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    CircularProgress,
    Box,
    Paper,
    Grid,
    Typography,
    Modal,
    TextField, // Added for Search
    InputAdornment
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import { AccountBalanceWallet, Cancel, Search } from '@mui/icons-material';

import { useState, useEffect, useMemo } from 'react'; // Added useMemo
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { errorAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    maxHeight: '90vh', // Ensure modal doesn't exceed screen height
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column' // Necessary for internal scrolling
};

const WalletListModal = ({ 
    walletListModalOpen,
    setWalletListModalOpen,
    customerRow
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);
    
    const [loading, setLoading] = useState(false);
    const [walletList, setWalletList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const getWalletList = async (passedCustomerID) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/Backend/WalletList', {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    customerID: passedCustomerID,
                    clientName: ''
                }
            });

            if (res.status === 200) {
                // Sort Descending by balance immediately on fetch
                const sortedData = res.data.sort((a, b) => b.balance - a.balance);
                setWalletList(sortedData);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (walletListModalOpen && customerRow?.id) {
            setSearchTerm(''); // Reset search when opening
            getWalletList(customerRow.id);
        }
    }, [walletListModalOpen, customerRow]);

    // Filter logic
    const filteredWallets = useMemo(() => {
        return walletList.filter(w => 
            w.walletTypeName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [walletList, searchTerm]);

    return (
        <Modal
            open={walletListModalOpen}
            onClose={() => setWalletListModalOpen(false)}
            aria-labelledby="modal-modal-title"
        >
            <Box sx={style}>
                {/* Header Section (Static) */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography id="modal-modal-title" variant="h3">
                        {t("customer.walletBalance")} - {customerRow?.name}
                    </Typography>
                    <Cancel 
                        onClick={() => setWalletListModalOpen(false)} 
                        sx={{ cursor: 'pointer', width: '30px', height: '30px', color: 'grey.500' }} 
                    />
                </Box>

                {/* Search Bar Section (Static) */}
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search wallet name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Content Section (Scrollable) */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredWallets.length > 0 ? (
                        <Grid container spacing={2}>
                            {filteredWallets.map((w, index) => (
                                <Grid item key={index} xs={12} sm={6} md={4}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            padding: '15px',
                                            transition: '0.3s',
                                            '&:hover': { boxShadow: 3 }
                                        }}
                                    >
                                        <Typography variant="caption" color="textSecondary">
                                            {w.walletTypeName}
                                        </Typography>
                                        <Typography variant="h3" sx={{ mt: 1, color: deepPurple[600] }}>
                                            {w.balance.toLocaleString()} 
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                            {searchTerm ? "No wallets match your search" : "No records found"}
                        </Box>
                    )}
                </Box>

                {/* Optional Footer */}
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button onClick={() => setWalletListModalOpen(false)} variant="contained" color="inherit">
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default WalletListModal;